import { createPublicClient, http, custom, createWalletClient, getAddress } from 'viem';
import { mainnet as ethereumMainnet, sepolia } from 'viem/chains';
import { Connection, PublicKey } from '@solana/web3.js';
import { getOrCreateWeb3Auth, createWeb3Session, invalidateWeb3Session } from './supabase';
import i18n from '../i18n';

// 本地存储键
const LOCAL_STORAGE_KEY = 'web3_user_profile';

// 获取验证消息模板
const getVerificationMessage = (nonce) => {
  const currentLang = i18n.language;
  let template;
  
  if (currentLang.startsWith('zh')) {
    // 中文消息模板
    template = `欢迎来到 Flatworld AI`;
  } else {
    // 英文消息模板（默认）
    template = `Welcome to Flatworld AI`;
  }
  
  console.log("getVerificationMessage", nonce)
  return template;
};

// 从localStorage读取用户信息
export const getStoredUser = () => {
  const storedUser = localStorage.getItem(LOCAL_STORAGE_KEY);
  return storedUser ? JSON.parse(storedUser) : null;
};

// 将用户信息存储到localStorage
export const storeUser = (user) => {
  if (user) {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  }
};

// 根据链ID获取对应的链配置
const getChainFromChainId = (chainId) => {
  // Sepolia 测试网的链ID为 11155111 (0xaa36a7)
  if (chainId === '0xaa36a7') {
    return sepolia;
  }
  // 默认使用以太坊主网
  return ethereumMainnet;
};

// 将Uint8Array转换为十六进制字符串
const uint8ArrayToHex = (arr) => {
  return Array.from(arr)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};

export const web3AuthService = {
  // 检查是否有以太坊钱包
  hasEthereumWallet: () => {
    return typeof window !== 'undefined' && window.ethereum;
  },

  // 检查是否有Solana钱包
  hasSolanaWallet: () => {
    return typeof window !== 'undefined' && window.solana;
  },

  // 连接以太坊钱包
  connectEthereumWallet: async () => {
    try {
      if (!web3AuthService.hasEthereumWallet()) {
        throw new Error('没有检测到以太坊钱包');
      }

      // 请求账户访问权限
      const accounts = await window.ethereum.request({ 
        method: 'eth_requestAccounts' 
      });
      
      if (!accounts || accounts.length === 0) {
        throw new Error('用户拒绝了钱包连接请求');
      }

      const address = getAddress(accounts[0]);
      
      // 创建钱包客户端
      const walletClient = createWalletClient({
        account: address,
        transport: custom(window.ethereum)
      });

      // 获取链ID
      const chainId = await window.ethereum.request({ method: 'eth_chainId' });
      
      // 获取对应的链配置
      const chain = getChainFromChainId(chainId);
      
      // 更新钱包客户端使用正确的链
      const updatedWalletClient = createWalletClient({
        account: address,
        chain,
        transport: custom(window.ethereum)
      });
      
      return {
        address,
        chainId,
        walletClient: updatedWalletClient,
        type: 'ethereum',
        chain
      };
    } catch (error) {
      console.error('连接以太坊钱包失败:', error);
      throw error;
    }
  },

  // 连接Solana钱包
  connectSolanaWallet: async () => {
    try {
      if (!web3AuthService.hasSolanaWallet()) {
        throw new Error('没有检测到Solana钱包');
      }

      // 请求连接
      const resp = await window.solana.connect();
      const publicKey = new PublicKey(resp.publicKey.toString());
      
      return {
        publicKey,
        type: 'solana'
      };
    } catch (error) {
      console.error('连接Solana钱包失败:', error);
      throw error;
    }
  },

  // 使用以太坊钱包签名消息进行认证
  signInWithEthereum: async (address, walletClient, chain = ethereumMainnet) => {
    try {
      // 获取或创建Web3认证记录，获取nonce
      const authRecord = await getOrCreateWeb3Auth('ethereum', address);
      
      // 生成要签名的消息
      const messageToSign = getVerificationMessage(authRecord.nonce);
      
      // 签名消息
      const signature = await walletClient.signMessage({
        account: address,
        message: messageToSign
      });

      // 创建Web3会话
      await createWeb3Session('ethereum', address, signature);

      // 构建用户对象
      const userProfile = {
        id: address,
        address,
        type: 'ethereum',
        network: chain.name,
        chainId: chain.id,
        profile: {
          name: `${address.substring(0, 6)}...${address.substring(address.length - 4)}`,
          avatar: null, // 不再使用avatarGenerator，而是使用WalletAvatar组件
        },
        auth: {
          signature,
          message: messageToSign,
          nonce: authRecord.nonce
        },
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      return userProfile;
    } catch (error) {
      console.error('以太坊签名认证失败:', error);
      throw error;
    }
  },

  // 使用Solana钱包签名消息进行认证
  signInWithSolana: async (publicKey) => {
    try {
      if (!window.solana) {
        throw new Error('没有检测到Solana钱包');
      }

      const address = publicKey.toString();
      
      // 获取或创建Web3认证记录，获取nonce
      const authRecord = await getOrCreateWeb3Auth('solana', address);
      
      // 生成要签名的消息
      const messageToSign = getVerificationMessage(authRecord.nonce);

      // 编码消息
      const encodedMessage = new TextEncoder().encode(messageToSign);
      
      // 请求签名
      const { signature } = await window.solana.signMessage(encodedMessage, 'utf8');
      
      // 将签名转换为十六进制字符串
      const signatureHex = uint8ArrayToHex(signature);
      
      // 创建Web3会话
      await createWeb3Session('solana', address, signatureHex);
      
      // 构建用户对象
      const userProfile = {
        id: address,
        address,
        type: 'solana',
        profile: {
          name: `${address.substring(0, 6)}...${address.substring(address.length - 4)}`,
          avatar: null, // 不再使用avatarGenerator，而是使用WalletAvatar组件
        },
        auth: {
          signature: signatureHex,
          message: messageToSign,
          nonce: authRecord.nonce
        },
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      return userProfile;
    } catch (error) {
      console.error('Solana签名认证失败:', error);
      throw error;
    }
  },

  // 断开钱包连接
  disconnectWallet: async () => {
    try {
      // 获取当前用户
      const user = getStoredUser();
      
      // 如果有用户，使其会话失效
      if (user && user.address) {
        await invalidateWeb3Session(user.address);
      }
      
      // 清除本地存储
      storeUser(null);
      
      // 对于某些钱包，可能需要调用特定的断开连接方法
      // 这里我们只清除本地存储，因为大多数钱包没有断开连接的API
      return { success: true };
    } catch (error) {
      console.error('断开钱包连接失败:', error);
      throw error;
    }
  }
};
